import { createElement, type ReactNode } from 'react';
import { toast } from 'sonner';

/**
 * The product's one way to say something that just happened (hotfix 2026-09-28): a toast in the bottom-right stack
 * (`components/AppToaster.tsx`), with a close button, gone by itself after a few seconds. Call sites use `notify`,
 * never sonner itself, so the durations and the dedupe rule live here.
 *
 * What is NOT a toast: a field or form error, an error inside an open dialog the person must fix there, a page's load
 * failure with its "Thử lại", and any explanation of what is on screen now — those stay inline (`Notice`).
 *
 * Dedupe: a toast's id defaults to its tone and words, so the same message said twice replaces itself instead of
 * stacking a copy; a caller that says different words about one thing passes its own `id`.
 */

/** How long a toast stays (ms). An error stays longer: it is the one the person may need to read twice. */
export const TOAST_MS = 5000;
export const ERROR_TOAST_MS = 8000;

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export type ToastOptions = {
  /** The same id replaces the toast on screen rather than adding one. */
  id?: string;
  /** Milliseconds; `Infinity` keeps it until it is closed or dismissed by id. */
  duration?: number;
  /** A button of the toast's own (e.g. "Hoàn tác"). */
  action?: ReactNode;
  icon?: ReactNode;
  onDismiss?: () => void;
  onAutoClose?: () => void;
};

/*
 * Our id → the id sonner holds for the toast now on screen. Sonner 2.0.8 keeps a closed toast for its 200 ms exit and
 * merges a new toast of the same id into it, so the new words leave with it. A toast that has gone — closed, hidden
 * by itself, dismissed — therefore gets a fresh sonner id next time; one still on screen is updated in place.
 */
const onScreen = new Map<string, string>();
let generation = 0;

function sonnerId(id: string): string {
  const current = onScreen.get(id);
  if (current !== undefined && toast.getToasts().some((shown) => shown.id === current)) return current;
  generation += 1;
  const next = `${id}#${generation}`;
  onScreen.set(id, next);
  return next;
}

function gone(id: string, shown: string): void {
  if (onScreen.get(id) === shown) onScreen.delete(id);
}

type SonnerOptions = Parameters<typeof toast>[1] & object;

function sonnerOptions(id: string, options: Omit<ToastOptions, 'id'>, fallback: number): SonnerOptions {
  const { duration, onDismiss, onAutoClose, ...rest } = options;
  const shown = sonnerId(id);
  return {
    ...rest,
    id: shown,
    duration: duration ?? fallback,
    onDismiss: () => { gone(id, shown); onDismiss?.(); },
    onAutoClose: () => { gone(id, shown); onAutoClose?.(); },
  };
}

function show(tone: ToastTone, text: string, options: ToastOptions = {}): string {
  const { id = `${tone}:${text}`, ...rest } = options;
  // Sonner's region is polite; a failure is announced at once, as the inline alert it replaces was.
  const title = tone === 'error' ? createElement('span', { role: 'alert' }, text) : text;
  toast[tone](title, sonnerOptions(id, rest, tone === 'error' ? ERROR_TOAST_MS : TOAST_MS));
  return id;
}

export const notify = {
  info: (text: string, options?: ToastOptions) => show('info', text, options),
  success: (text: string, options?: ToastOptions) => show('success', text, options),
  warning: (text: string, options?: ToastOptions) => show('warning', text, options),
  error: (text: string, options?: ToastOptions) => show('error', text, options),
  /** A toneless toast whose body is the caller's (the undo offer); it needs an `id`, there are no words to key on. */
  message: (body: ReactNode, options: ToastOptions & { id: string }): string => {
    const { id, ...rest } = options;
    toast(body, sonnerOptions(id, rest, TOAST_MS));
    return id;
  },
  /** Closes one toast by id, or every toast. */
  dismiss: (id?: string) => {
    if (id === undefined) { onScreen.clear(); toast.dismiss(); return; }
    const shown = onScreen.get(id);
    onScreen.delete(id);
    if (shown !== undefined) toast.dismiss(shown);
  },
};
